import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  AppDatePicker,
  AppInput,
  AppRemixIcon,
  AppSingleSelect,
} from '@/layout/AppComponents';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import ReactDOMServer from 'react-dom/server';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';
import Translator from '../../../../components/helpers/Translator';
import { axiosPath, path } from '../../../../../routing/helpers/router';
import { getTotalEnergiesRiskActionPlanFilterInitialValues } from '../../../../../minions/forms';
import AuxSidebar from '../../../../Sidebar/AuxSidebar';
import useIsMounted from '../../../../components/hooks/useIsMounted';
import TotalEnergiesRiskRiskSelect from '../../../../components/form/Selects/TotalEnergiesRiskRiskSelect';
import ResponsibleSelect from '../../../../components/form/Selects/ResponsibleSelect';
import TotalEnergiesRiskProcessSelect from '../../../../components/form/Selects/TotalEnergiesRiskProcessSelect';

export default function ActionPlanFilterSidebarBlock() {
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { moduleBridgeHex, filtersHex } = useParams();
  const auxSidebar = useSelector(
    (state) => state.currentPage?.auxSidebar?.opened
  );
  const filters = useSelector((state) => state.currentPage?.actionPlanFilters);
  const [openOnce, setOpenOnce] = useState(true);
  const locale = useSelector((state) => state.global?.urlData?.lang);
  const [noYesOptions] = useState([
    {
      label: <Translator file="user" name="general.no" />,
      value: 0,
    },
    {
      label: <Translator file="user" name="general.yes" />,
      value: 1,
    },
  ]);

  const handleAuxSidebar = () => {
    if (!openOnce) setOpenOnce(true);
    if (auxSidebar !== 'filters') {
      dispatch(currentPageAction.change('auxSidebar', { opened: 'filters' }));
    } else {
      dispatch(currentPageAction.change('auxSidebar', { opened: null }));
    }
  };

  const goToActionPlans = (hexCode) =>
    navigate(
      path({
        name: 'user_module_totalEnergiesRisk_actionPlan_list',
        params: {
          moduleBridgeHex,
          page: 1,
          filtersHex: hexCode,
        },
      })
    );

  // > GENERATE FILTER HEX CODE
  const generateFilterHexCode = async (values, setSubmitting) =>
    axios
      .post(
        axiosPath(
          'api_r_user_module_totalEnergiesRisk_risk_generateFilterHexCode'
        ),
        { moduleBridgeHex, filters: values }
      )
      .then(({ data }) => {
        setSubmitting(false);
        goToActionPlans(data.hexCode);
        dispatch(currentPageAction.change('auxSidebar', { opened: null }));
      });

  const handleOnSubmit = (values, { setSubmitting }) => {
    generateFilterHexCode(values, setSubmitting);
  };
  // < GENERATE FILTER HEX CODE

  // > FILTERS
  const getFilters = useCallback(
    async () =>
      axios
        .post(
          axiosPath('api_r_user_module_totalEnergiesRisk_risk_getFilters'),
          {
            moduleBridgeHex,
            filtersHex,
          }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            const f = getTotalEnergiesRiskActionPlanFilterInitialValues();
            dispatch(
              currentPageAction.change(
                'actionPlanFilters',
                { ...f, ...data },
                true
              )
            );
          }
        }),
    [moduleBridgeHex, filtersHex]
  );

  useEffect(() => {
    if (filtersHex !== '0') {
      getFilters();
    } else {
      dispatch(
        currentPageAction.change(
          'actionPlanFilters',
          getTotalEnergiesRiskActionPlanFilterInitialValues(),
          true
        )
      );
    }
  }, [filtersHex]);
  // < FILTERS

  return (
    <>
      <li
        className={auxSidebar && auxSidebar === 'filters' ? 'active' : ''}
        onClick={() => handleAuxSidebar()}
      >
        <span className="sidebar-element">
          <AppRemixIcon icon="equalizer" className="sidebar-icon" />{' '}
          <span className="sb-title">
            <Translator file="module-legalCompliance" name="filtering" />
          </span>
        </span>
      </li>
      <AuxSidebar
        title={<Translator file="module-legalCompliance" name="filtering" />}
        isOpen={auxSidebar && auxSidebar === 'filters'}
        width="350px"
        position="right"
        onClose={handleAuxSidebar}
      >
        {openOnce && (
          <>
            {filters && (
              <Formik
                validateOnChange={false}
                validateOnBlur={false}
                enableReinitialize
                initialValues={filters}
                onSubmit={handleOnSubmit}
              >
                {({
                  setFieldValue,
                  handleSubmit,
                  values,
                  errors,
                  isSubmitting,
                }) => (
                  <>
                    <div className="d-flex mt-3 mb-3">
                      <span
                        className="btn btn-info btn-block"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                      >
                        <Translator
                          file="module-legalCompliance"
                          name="filtering"
                        />
                      </span>
                      {filtersHex !== '0' && (
                        <span
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="delete.filters"
                            />
                          )}
                          className="btn btn-outline-danger ml-1"
                          onClick={() =>
                            navigate(
                              path({
                                name: 'user_module_totalEnergiesRisk_asctionPlan_list',
                                params: {
                                  moduleBridgeHex,
                                  page: 1,
                                  filtersHex: 0,
                                },
                              })
                            )
                          }
                        >
                          <AppRemixIcon icon="delete-bin-6" />
                        </span>
                      )}
                    </div>
                    <AppInput
                      title="Código"
                      icon={<AppRemixIcon icon="fingerprint" />}
                      value={values.code || ''}
                      onChange={(opt) => setFieldValue('code', opt)}
                      isClearable
                    />
                    <AppInput
                      title="Descripción"
                      value={values.description || ''}
                      error={errors.description}
                      onChange={(opt) => setFieldValue('description', opt)}
                      isClearable
                    />
                    <TotalEnergiesRiskRiskSelect
                      value={values?.riskIDs || []}
                      error={errors?.riskIDs}
                      onHandleChange={(opt) => setFieldValue('riskIDs', opt)}
                      hasSearchBox
                      isClearable
                      isMulti
                    />
                    <TotalEnergiesRiskProcessSelect
                      title="Procesos"
                      value={values?.process}
                      error={errors?.process}
                      onHandleChange={(opt) => {
                        setFieldValue('process', opt);
                      }}
                      hasSearchBox
                      hasSelectOptions
                      isMulti
                      onlyParents
                    />
                    <ResponsibleSelect
                      value={values.responsibleID}
                      error={errors?.responsibleID}
                      onHandleChange={(opt) =>
                        setFieldValue('responsibleID', opt)
                      }
                      apiCall="api_r_user_module_totalEnergiesRisk_control_getResponsibles"
                      hasSearchBox
                      isClearable
                    />
                    <AppDatePicker
                      locale={locale}
                      title="Fecha de creación desde"
                      value={values?.fromDateStamp}
                      onChange={(opt) => setFieldValue('fromDateStamp', opt)}
                      isClearable
                      showYearDropdown
                      showMonthDropdown
                    />
                    <AppDatePicker
                      locale={locale}
                      title="Fecha de creación hasta"
                      value={values?.toDateStamp}
                      onChange={(opt) => setFieldValue('toDateStamp', opt)}
                      isClearable
                      showYearDropdown
                      showMonthDropdown
                    />
                    <AppDatePicker
                      locale={locale}
                      title="Fecha de inicio desde"
                      value={values?.fromStartDateStamp}
                      onChange={(opt) =>
                        setFieldValue('fromStartDateStamp', opt)
                      }
                      isClearable
                      showYearDropdown
                      showMonthDropdown
                    />
                    <AppDatePicker
                      locale={locale}
                      title="Fecha de inicio hasta"
                      value={values?.toStartDateStamp}
                      onChange={(opt) => setFieldValue('toStartDateStamp', opt)}
                      isClearable
                      showYearDropdown
                      showMonthDropdown
                    />
                    <AppDatePicker
                      locale={locale}
                      title="Fecha de terminación desde"
                      value={values?.fromEndDateStamp}
                      onChange={(opt) => setFieldValue('fromEndDateStamp', opt)}
                      isClearable
                      showYearDropdown
                      showMonthDropdown
                    />
                    <AppDatePicker
                      locale={locale}
                      title="Fecha de terminación hasta"
                      value={values?.toEndDateStamp}
                      onChange={(opt) => setFieldValue('toStartDateStamp', opt)}
                      isClearable
                      showYearDropdown
                      showMonthDropdown
                    />
                    <AppDatePicker
                      locale={locale}
                      title="Fecha de terminación desde"
                      value={values?.fromEndDateStamp}
                      onChange={(opt) => setFieldValue('fromEndDateStamp', opt)}
                      isClearable
                      showYearDropdown
                      showMonthDropdown
                    />
                    <AppDatePicker
                      locale={locale}
                      title="Fecha de terminación hasta"
                      value={values?.toStartDateStamp}
                      onChange={(opt) => setFieldValue('toStartDateStamp', opt)}
                      isClearable
                      showYearDropdown
                      showMonthDropdown
                    />
                    <AppDatePicker
                      locale={locale}
                      title="Fecha última modificación desde"
                      value={values?.fromModificationDateStamp}
                      onChange={(opt) => setFieldValue('fromEndDateStamp', opt)}
                      isClearable
                      showYearDropdown
                      showMonthDropdown
                    />
                    <AppDatePicker
                      locale={locale}
                      title="Fecha última modificación hasta"
                      value={values?.toModificationDateStamp}
                      onChange={(opt) => setFieldValue('toStartDateStamp', opt)}
                      isClearable
                      showYearDropdown
                      showMonthDropdown
                    />
                    <AppSingleSelect
                      options={noYesOptions}
                      title="Mostrar anulados"
                      value={values?.active}
                      error={errors?.active}
                      onChange={(opt) => {
                        setFieldValue('active', opt);
                      }}
                    />
                  </>
                )}
              </Formik>
            )}
          </>
        )}
      </AuxSidebar>
    </>
  );
}
